import {useEffect, useRef} from 'react';

import {sendDesktopAnalyticsPerSession} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';

import {GENERAL_EXPERIMENT_INDICATION} from 'constants/analytics/events/general';
import {ENDPOINTS} from 'constants/endpoints';
import {sagaMiddleware} from 'desktop/store';
import {apiConfig} from 'libs/api/api';
import {FlickerAnimation} from 'libs/flicker/flicker';
import {flags} from 'libs/rollout';
import {deleteParam, getUrlParams} from 'libs/url/url';

import {startCheckoutRootSaga} from '../checkout/store/sagas';

import {PropsType} from './cart';
import cartRootSaga from './store/sagas';

/**
 * startQuoteSaga
 * @param itemToken
 */
async function startQuoteSagaFlow(startQuoteSaga: () => void, itemToken?: string) {
    if (itemToken) {
        deleteParam('itemtoken', true);
        await apiConfig.instanceGusaBackend.post(ENDPOINTS.CART_REORDER, {
            token: itemToken,
        });
        startQuoteSaga();
        return;
    }

    startQuoteSaga();
}

/**
 * Side effects for Cart page
 * isContactsInCart
 */
export const useSideEffects = ({
    cartUpsells,
    changeItemQty,
    coupons,
    getCartQuoteSaga: triggerGetCartQuoteSaga,
    getQuote: triggerGetQuote,
    isCheckOrderRequestDone,
    isContactsInCart,
    itemsCount,
}: PropsType) => {
    /**
     * Send analytics data if totals changed
     */
    const prevListenPropsStatuses = useRef([
        cartUpsells.status,
        coupons.status,
        changeItemQty.status,
    ]);

    useEffect(() => {
        const task = sagaMiddleware.run(cartRootSaga);
        startCheckoutRootSaga();

        return () => {
            task.cancel();
        };
    }, []);

    useEffect(() => {
        const listenPropsStatuses = [cartUpsells.status, coupons.status, changeItemQty.status];
        listenPropsStatuses.forEach((nextPropStatus, index) => {
            if (
                (nextPropStatus === 'fulfilled' || nextPropStatus === 'rejected') &&
                nextPropStatus !== prevListenPropsStatuses.current[index]
            ) {
                triggerGetQuote({});
            }
        });
        prevListenPropsStatuses.current = listenPropsStatuses;
    }, [triggerGetQuote, cartUpsells.status, coupons.status, changeItemQty.status]);

    /**
     * Update Flicker unfocused title if cart items count > 0
     */
    useEffect(() => {
        const flicker = FlickerAnimation.getInstance();
        if (itemsCount > 0) {
            flicker.updateUnFocusedTitle(`You left ${itemsCount} item(s) in cart 🛒`);
        } else {
            flicker.resetUnFocusedTitle();
        }
    }, [itemsCount]);

    useEffect(() => {
        (async () => {
            const itemToken = getUrlParams()?.itemtoken ?? false;

            if (!flags.dReturningCustomerCL.isEnabled()) {
                await startQuoteSagaFlow(triggerGetCartQuoteSaga, itemToken[0]);
                return;
            }

            if (isCheckOrderRequestDone)
                await startQuoteSagaFlow(triggerGetCartQuoteSaga, itemToken[0]);
        })();
    }, [isCheckOrderRequestDone, triggerGetCartQuoteSaga]);

    useEffect(() => {
        if (!isContactsInCart) return;

        sendDesktopAnalyticsPerSession({
            event: {
                ...GENERAL_EXPERIMENT_INDICATION,
                action: 'autoApplyCouponsCLCart',
            },
            isInteraction: false,
            label: `${flags.autoApplyCouponsCLCart.isEnabled()}`,
        });
    }, [isContactsInCart]);
};
